import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowLeft, Sparkles, BookOpen } from "lucide-react";

export default function ChapterReader() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/kb/chapters/${id}`).then(r => setData(r.data)).catch(() => setData({ error: true }));
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!data) return <div className="space-y-3"><Skeleton className="h-10" /><Skeleton className="h-96" /></div>;
  if (data.error) return <div className="text-sm text-muted-foreground">Chapter not found.</div>;

  const { chapter, body, siblings } = data;

  // Paragraph split and simple formatting
  const paras = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  const askTribunal = () => {
    nav(`/app/tribunal?q=${encodeURIComponent(`In the chapter "${chapter.title}", summarize the key operating guidance.`)}`);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10" data-testid="chapter-reader">
      <div className="lg:col-span-9 max-w-3xl">
        <Button variant="ghost" onClick={() => nav(-1)} data-testid="chapter-back"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <div className="label-caps mt-6 mb-2">{chapter.section}</div>
        <h1 className="text-3xl md:text-5xl tracking-tighter font-semibold leading-tight">{chapter.title}</h1>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(chapter.pillars || []).map((p) => (
            <Badge key={p} variant="outline" className="text-[10px] font-mono uppercase">{p}</Badge>
          ))}
          <Badge variant="secondary" className="text-[10px] font-mono">Volume {chapter.part_num}</Badge>
        </div>
        <div className="mt-10 space-y-5 text-[15px] leading-relaxed max-w-prose">
          {paras.slice(0, 400).map((p, i) => {
            // Headings heuristic: short lines ending without punctuation that aren't bullets
            if (p.length < 90 && /^[A-Z0-9]/.test(p) && !p.endsWith(".") && !p.startsWith("•") && !p.startsWith("-")) {
              return <h2 key={i} className="text-xl font-medium tracking-tight mt-10 mb-3">{p}</h2>;
            }
            if (p.startsWith("•") || /^\d+\./.test(p)) {
              return <p key={i} className="pl-4 border-l border-border text-foreground/90">{p}</p>;
            }
            return <p key={i} className="text-foreground/90">{p}</p>;
          })}
        </div>
      </div>

      <aside className="lg:col-span-3 space-y-6 lg:sticky lg:top-24 self-start">
        <Card className="p-5">
          <div className="label-caps mb-3">Explore further</div>
          <Button onClick={askTribunal} className="w-full rounded-full" data-testid="ask-tribunal">
            <Sparkles className="h-4 w-4 mr-1" /> Ask Tribunal about this
          </Button>
        </Card>
        <Card className="p-5">
          <div className="label-caps mb-3 flex items-center gap-1"><BookOpen className="h-3 w-3" />In this volume</div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto no-scrollbar">
            {(siblings || []).map((s) => (
              <Link key={s.id} to={`/app/learn/chapter/${s.id}`}
                className={`block text-xs py-1.5 border-l-2 pl-2 transition-colors duration-200 ${
                  s.id === chapter.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`} data-testid={`sibling-${s.id}`}>
                {s.title}
              </Link>
            ))}
          </div>
        </Card>
      </aside>
    </div>
  );
}
